import { UsersServiceType } from "./users.service.type";
import { AppType, APP_TYPE } from "../app.type";
import { hasChanges } from "../utils/app.utils";
import { Injectable, Inject } from "@angular/core";
import {
  deleteUser,
  doc2Model,
  getComponentResponsibles,
  getCustomerWithLongestPass,
  getUserByIdAsync,
  getUserNamesWithIds,
  getUsersCompressed,
  getUsersWithRoles,
  model2Doc,
  saveNewUser,
  setUserLanguage,
  updateUser,
} from "../api.service";
import {
  User,
  UserCompressed,
  UserData,
  UserOptions,
} from "../../../../shared/models/user";
import {
  checkPasswordPolicy,
  generateRandomPasswordSelection,
  getMinPassLengthByCustomer,
} from "../password/password-policy";
import { Customer } from "../../../../shared/types/customers";
import { environment } from "../../environments/environment";
import { StringUtils } from "../../../../shared/utils/string.utils";
import { writeConfigFile } from "../export/config-export";
import { BehaviorSubject } from "rxjs";

export const NO_ERROR = "No error";

@Injectable()
export class UsersService implements UsersServiceType {
  userSubject: BehaviorSubject<User> = new BehaviorSubject<User>({} as User);
  users: UserCompressed[] = [];
  cleanUsers: UserCompressed[] = [];
  userIds: string[] = [];
  currentUser = new User();
  currentUserModel: any = {} as User;
  cleanUserModel: any = {} as User;
  currentId = "";
  currentUserName = "";
  isNew = false;
  componentResponsibles: string[] = [];
  userRoles: UserOptions[] = [];
  userSettingChanges = false;
  mfaPreferences = false;
  creationOpen = false;
  userNames: string[] = [];
  allUserNames = [];
  btnClicked: boolean = false;

  constructor(@Inject(APP_TYPE) private app: AppType) {}

  initCreation() {
    this.creationOpen = true;
    const id = this.app.createId();
    this.app.unlockedId = id;
    this.currentId = id;
    this.isNew = true;
    const newUser = new User();
    this.app.users.currentUserModel = doc2Model("user", newUser) as User;
    /** set default language to default of the user who is creating the user */
    this.app.users.currentUserModel[this.app.fieldId.user.language] =
      this.app.state.language;
    /** auto generate password based on the password length of the customer */
    this.setPasswordExpireDate();
    /** set password expired date now + 6 months */
    this.generatePassword();
  }

  async getListOfUsers() {
    this.users = await getUsersCompressed(this.app.customers.expectCurrent);
    this.userIds = this.users.map((user) => user.id);
    this.userNames = this.users.map((user) => user.name);
    this.cleanUsers = this.users;
  }

  async getAllUserNames() {
    (this.allUserNames as any) = (
      await getUserNamesWithIds(this.app.customers.expectCurrent)
    ).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }

  async getUserByIdAsync(userId: string) {
    this.currentUser = await getUserByIdAsync(
      this.app.customers.expectCurrent,
      userId
    );
    this.currentId = this.currentUser._id as any;
    this.currentUserModel = doc2Model("user", this.currentUser);
    this.cleanUserModel = doc2Model("user", this.currentUser);
    this.currentUserName = this.currentUser.name;
    this.userSubject.next(this.currentUser);
    return this.currentUser;
  }
  async getComponentResponsibles() {
    if (this.app.customers.expectCurrent !== Customer.DB) {
      return;
    }

    this.componentResponsibles = await getComponentResponsibles(
      this.app.customers.expectCurrent
    );
  }

  async getUsersWithRoles() {
    this.userRoles = await getUsersWithRoles(this.app.customers.expectCurrent);
    this.userRoles = this.sortOptionsAlphabetically(this.userRoles);
  }

  getOptionName(id: string) {
    if (StringUtils.isNullOrEmpty(id)) {
      return "";
    }
    let user: UserData = this.allUserNames.filter(
      (user: UserData) => user.id === id
    )[0];
    if (user == null) {
      return id;
    }
    return user.name;
  }

  getOptionId(name: string) {
    if (StringUtils.isNullOrEmpty(name)) {
      return "";
    }
    let user = this.app.users.userRoles.filter((user) => user.name === name)[0];
    if (user == null) {
      return name;
    }
    return user.id;
  }

  getOptionsById(name: string) {
    /** used only for table filtering  */
    if (StringUtils.isNullOrEmpty(name)) {
      return [];
    }
    let users = this.allUserNames.filter((user: UserData) =>
      user.name.toLowerCase().includes(name.toLowerCase())
    );
    if (users == null) {
      return [];
    }
    return users.map((user: UserData) => user.id);
  }

  getUserRole(userName: string) {
    const user = this.userRoles.find((u: UserOptions) => u.name === userName);

    if (user == null || user.roles == null) {
      return "";
    }

    return user.roles[0].split("-")[1];
  }

  modelHasChanges() {
    const userChanges = hasChanges(this.cleanUserModel, this.currentUserModel);
    this.userSettingChanges = hasChanges(
      this.app.userSettings.cleanUserEmailPreferences,
      this.app.userSettings.currentUserEmailPreferences
    );

    return userChanges || this.userSettingChanges;
  }

  getOptions(role?: string, jobTitle?: string) {
    const currentRole = this.app.customers.expectCurrent + "-" + role;
    const isProductionMode = environment.production;
    let users: UserOptions[] = [];

    if (this.userRoles.length > 0) {
      if (currentRole !== undefined && jobTitle == undefined) {
        users = this.userRoles.filter((user: UserOptions) =>
          user.roles.includes(currentRole)
        );
      } else {
        if (jobTitle == null) {
          return;
        }

        users = this.userRoles.filter(
          (user: UserOptions) =>
            user.jobTitle != null && user.jobTitle.includes(jobTitle)
        );
      }
    }

    let result: UserOptions[] = [];
    if (isProductionMode) {
      /** https://app.asana.com/0/1203066793253695/1199525579744726  */
      /** do not show amsys users in dropdowns in production mode */
      result = users.filter(
        (user: any) => !user.name.toLowerCase().includes("amsys")
      );
      // !user.email.includes("@am-sys.com")
    } else {
      result = this.sortOptionsAlphabetically(users);
    }

    return this.sortOptionsAlphabetically(result) as any;
  }

  sortOptionsAlphabetically(options: any[]) {
    // replace german letters so the sorting can be done correctly
    const format = (s: string): string =>
      s
        .toLowerCase()
        .replace(/ä/g, "a")
        .replace(/ö/g, "o")
        .replace(/ü/g, "u")
        .replace(/ß/g, "ss");

    const sorted = options.sort((a: any, b: any) => {
      const textA = format(a.name);
      const textB = format(b.name);
      if (textA < textB) {
        return -1;
      }
      if (textA > textB) {
        return 1;
      }
      return 0;
    });
    return sorted;
  }

  async checkPasswordPolicy(user: User, isNewUser: boolean) {
    let customer = this.app.customers.expectCurrent;

    if (!isNewUser) {
      customer = await getCustomerWithLongestPass(user._id as any);
    }

    const result = await checkPasswordPolicy(this.app.text, user, customer);

    if (result !== NO_ERROR) {
      this.app.hasError = true;
      this.app.errorText = result;
    }

    return result;
  }

  async generatePassword() {
    let length: number = await getMinPassLengthByCustomer(
      this.app.customers.expectCurrent
    );
    const password = generateRandomPasswordSelection(length);
    this.currentUserModel["user.password"] = password;
    this.currentUserModel["user.retypePassword"] = password;
  }

  async save() {
    this.app.hasError = false;
    let result: any;
    this.btnClicked = true;

    const passwordPolicy = StringUtils.isNullOrEmpty(
      this.currentUserModel["user.password"]
    )
      ? NO_ERROR
      : await this.checkPasswordPolicy(
          model2Doc("user", this.currentUserModel),
          this.isNew
        );

    if (this.isNew) {
      // Create user
      this.currentUserModel["user.name"] =
        this.currentUserModel["user.firstName"] +
        " " +
        this.currentUserModel["user.lastName"];
      this.currentUserModel["user.email"] =
        this.currentUserModel["user.email"].toLowerCase();
      this.currentUserModel["user.create_user_id"] = this.app.state.userId;
      this.currentUserModel["user.create_time"] = new Date().getTime();
      console.log(this.currentUserModel);

      if (passwordPolicy === NO_ERROR) {
        this.currentUserModel["user.forcePasswordChangeOnFirstLogin"] = true;
        const userDoc = model2Doc("user", this.currentUserModel);
        result = await saveNewUser(this.app.customers.expectCurrent, userDoc);
      }
    } else {
      // Update user
      if (passwordPolicy === NO_ERROR) {
        this.currentUserModel["user.update_user_id"] = this.app.state.userId;
        this.currentUserModel["user.update_time"] = new Date().getTime();
        const userDoc = model2Doc("user", this.currentUserModel);
        delete userDoc._rev;
        result = await updateUser(this.app.customers.expectCurrent, userDoc);
      }
    }

    if (!result.error) {
      this.app.hasSuccess = true;

      if (this.isNew) {
        // the admin has the option to configure mfaPreferences when creating a user
        this.app.successText = this.app.text.user.successUserCreated;
        // this.app.userSettings.setMFAPreferences(userDoc.name);
      } else {
        this.app.successText = this.app.text.user.succesUserUpdated;
        this.currentUserModel = doc2Model("user", result);
        this.cleanUserModel = doc2Model("user", result);
        if (this.userSettingChanges) {
          await this.app.userSettings.updateSettings();
        }
      }
      this.creationOpen = false;
      await this.getListOfUsers();

      this.app.unlockedId = null;
      setTimeout(() => {
        this.app.hasSuccess = false;
      }, 3000);
    } else {
      if (result.error === "email exists") {
        this.app.hasSuccess = false;
        this.app.hasError = true;
        this.app.errorText = this.app.text.user.emailNotAccepted;
      } else {
        this.app.hasSuccess = false;
        this.app.hasError = true;
        this.app.errorText = this.app.text.user.passwordUsed;
      }
      setTimeout(() => {
        this.app.hasError = false;
      }, 3000);
    }
  }

  setPasswordExpireDate() {
    // Set passwordExpireDate 6 months starting from today
    if (this.currentUserModel == null) {
      return;
    }

    if (this.currentUserModel["user.passwordNeverExpire"]) {
      return;
    }

    const expiryDate = this.currentUserModel["user.passwordExpireDate"];
    let date: number = expiryDate || 0;
    if (
      ((!this.isNew &&
        !StringUtils.isNullOrEmpty(this.currentUserModel["user.password"])) ||
        expiryDate == null ||
        expiryDate === 0) &&
      date <= 0
    ) {
      const d = new Date();
      d.setMonth(d.getMonth() + 6);
      date = d.getTime();
    }

    this.currentUserModel["user.passwordExpireDate"] = date;
    return;
  }

  /**
   * Store the user's preferred language in the user document
   * @param language The language to be stored
   */
  async setLanguage(language: string): Promise<void> {
    if (this.app.state.userId != null) {
      await setUserLanguage(this.app.state.userId, language);
    }
    this.app.header.getHeaderTitle(this.app.state.inboxType);
  }

  async deactivateUser() {
    this.app.unlockedId = this.currentUser.name;
    this.currentUserModel[this.app.fieldId.user.active] = false;
    await this.save();
  }

  async delete() {
    let result: any;
    const customer = this.app.customers.expectCurrent;
    const doc: User = this.currentUser;
    delete doc._rev;
    if (doc._id != null) {
      result = await deleteUser(customer, doc._id);
      this.app.unlockedId = null;
    }
    if (result) {
      this.app.hasSuccess = true;
      this.app.successText = this.app.text.user.deleted;
    }
    if (this.currentUser.name === this.app.user) {
      this.app.auth.logout();
    }

    this.creationOpen = false;
    await this.getListOfUsers();
    setTimeout(() => {
      this.app.hasSuccess = false;
    }, 3000);
    // this.app.routing.navigateUsers();
  }

  isDisabled() {
    const requiredFields = this.app.field.getInvalidFields(
      "user",
      this.currentUserModel
    );

    if (!this.isEditMode()) {
      return true;
    }

    if (requiredFields.length === 0 && this.isNew) {
      return false;
    }

    if (requiredFields.length === 0 && this.modelHasChanges()) {
      return false;
    } else {
      return true;
    }
  }

  hasEditPermission() {
    const { permission } = this.app;

    if (permission.user.manage) {
      return true;
    }
    return false;
  }

  setRequiredFields() {
    const fieldId = this.app.fieldId.user.name;
    this.app.field.resetAllRequiredFieldSettings("user");
    if (!this.isNew) {
      this.app.field.getFieldSettings(fieldId).mutable = false;
      this.app.field.getFieldSettings("user.password").required = false;
      this.app.field.getFieldSettings("user.retypePassword").required = false;
      this.app.field.getFieldSettings("user.email").mutable = false;
      this.app.field.getFieldSettings("user.roles").required = true;
      // this.app.field.getFieldSettings("user.firstName").mutable = false;
      // this.app.field.getFieldSettings("user.lastName").mutable = false;
    } else {
      this.app.field.getFieldSettings(fieldId).mutable = true;
      this.app.field.getFieldSettings("user.firstName").required = true;
      this.app.field.getFieldSettings("user.lastName").required = true;
      this.app.field.getFieldSettings("user.email").mutable = true;
      this.app.field.getFieldSettings("user.email").required = true;
      this.app.field.getFieldSettings("user.roles").required = true;
      this.app.field.getFieldSettings("user.language").required = true;
      this.app.field.getFieldSettings("user.password").required = true;
      this.app.field.getFieldSettings("user.retypePassword").required = true;
      this.app.field.getFieldSettings("user.name").regex = "^[0-9a-zA-Z '-]+$";
    }
  }

  getCleanUserRole(role: string): string {
    if (role.includes(this.app.customers.expectCurrent)) {
      return role.split("-")[1];
    } else {
      return "";
    }
  }

  async exportData() {
    await writeConfigFile(this.app, "user");
  }

  private checkUsername(username: string) {
    const format: RegExp = /[\t!^()\=\[\]{};':"\\|<>\/?@#$%*]+/;
    if (StringUtils.containsSpecialCharacter(username, format)) {
      this.app.hasError = true;
      this.app.errorText = this.app.text.user.usernameNotAccepted;
      return this.app.text.user.usernameNotAccepted;
    } else {
      return NO_ERROR;
    }
  }

  private isEditMode() {
    if (this.app.unlockedId === this.currentId) {
      return true;
    }
    if (this.isNew) {
      return true;
    }
    return false;
  }
}
